import { createContext } from 'react';

import type { ResolvedConsent } from './consent-cookie';
import type { ConsentPromptMode } from './consent-prompt-policy';
import type {
  ConsentChoices,
  ConsentDecisionSource,
  ConsentSignals,
} from './consent-signals';

/**
 * Consent for the current visitor, split by what each half is for.
 *
 * Two different things can be called "the consent state": what is in force for
 * this pageview, which includes the region's implied defaults, and what the
 * visitor has actually answered. They are separate fields here, and the naming
 * is the contract:
 *
 * - `signals` is the effective state. The implied overlay is folded in and
 *   `unset` is already collapsed to `denied`. Destinations read this.
 * - `answers` (and `stored`) is what the visitor decided, and nothing else.
 *   Anything that persists consent, seeds a toggle, or decides whether to ask
 *   reads this.
 *
 * An overlay that reached a writer would be stamped as a decision the visitor
 * never made, so it never appears outside `signals`.
 */
export type ConsentContextValue = {
  /**
   * False during SSR and the first client render, before cookies can be
   * read. Consumers must not act on `signals` until this is true, or they
   * would publish a deny-all state over a real stored consent.
   */
  isResolved: boolean;
  /** The stored record, or `null` when the user has never chosen. */
  stored: ResolvedConsent | null;
  /**
   * Effective signals for destinations, including any implied default in force
   * for this pageview. `unset` has already been collapsed to `denied` here;
   * this is the gtag boundary.
   */
  signals: ConsentSignals;
  /**
   * The categories the visitor has answered, and `unset` for the rest — the
   * stored record's own choices, with no implied default folded in. This is
   * what a writer persists and what a toggle is seeded from.
   */
  answers: ConsentChoices;
  /** What (if anything) to prompt with. */
  promptMode: ConsentPromptMode;
  /** Records a decision at the current consent version. */
  save: (choices: ConsentChoices, src: ConsentDecisionSource) => void;
};

export const ConsentContext = createContext<ConsentContextValue | null>(null);
